import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain, Zap, AlertCircle, Clock } from "lucide-react";
import type { AIMetrics } from "@/services/analyticsService";

interface AIMetricsRowProps {
  metrics: AIMetrics;
}

export const AIMetricsRow = ({ metrics }: AIMetricsRowProps) => {
  const getRiskScoreColor = (score: number) => {
    if (score < 30) return "text-green-600";
    if (score < 60) return "text-yellow-600";
    return "text-red-600";
  };

  const formatTimeAgo = (date: Date | null) => {
    if (!date) return "Never";
    const hours = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">AI Analyses Run</CardTitle>
          <Brain className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{metrics.totalAIAnalyses}</div>
          <p className="text-xs text-muted-foreground">
            Total deals analyzed
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg AI Risk Score</CardTitle>
          <Zap className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getRiskScoreColor(metrics.avgAIRiskScore)}`}>
            {metrics.avgAIRiskScore}/100
          </div>
          <p className="text-xs text-muted-foreground">
            {metrics.avgAIRiskScore < 30 ? 'Low risk portfolio' : metrics.avgAIRiskScore < 60 ? 'Moderate risk' : 'High risk detected'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">High-Risk Flagged</CardTitle>
          <AlertCircle className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-destructive">{metrics.aiHighRiskFlagged}</div>
          <p className="text-xs text-muted-foreground">
            Deals requiring review
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Last Analysis</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatTimeAgo(metrics.lastAnalyzedAt)}</div>
          <p className="text-xs text-muted-foreground">
            Most recent AI check
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
